/* ============================================================
   Zefire — GSAP scroll-animation layer  v2
   ------------------------------------------------------------
   Declarative, reduced-motion-aware. No per-section JS.

   Every animation repeats on every scroll pass:
   • Scrolling DOWN into view  → plays forward (reveal)
   • Scrolling UP out of view  → reverses (hide)
   • Scrolling DOWN again       → plays forward again
   (hero-entrance is the only one-time animation — fires on load)

   ATTRIBUTE REFERENCE
   ───────────────────
   data-zf-anim="fade-up"           fade + rise from below
   data-zf-anim="fade-down"         fade + drop from above
   data-zf-anim="fade"              opacity only
   data-zf-anim="scale-in"         scale 0.92→1 + fade
   data-zf-anim="clip-left"         clipPath wipe reveal left→right
   data-zf-anim="scale-x"           scaleX 0→1 (gold rule lines)
   data-zf-anim="counter"           integer count-up to data-zf-target
   data-zf-anim="stagger"           container; children stagger in
     └ [data-zf-stagger-item]       explicit child targets (fallback: all children)
   (Hero entrance is now pure CSS — no attribute needed)

   MODIFIERS (on any element)
   data-zf-anim-delay="0.15"        seconds before start
   data-zf-anim-duration="0.9"      seconds
   data-zf-anim-y="40"              px travel for fade-up/down
   data-zf-stagger="0.08"           seconds between stagger items
   data-zf-target="2450"            final value for counter
   data-zf-suffix="%"               suffix appended to counter value
   ============================================================ */
(function () {
  'use strict';

  var EASE_OUT  = 'power3.out';
  var EASE_EXPO = 'expo.out';
  var START     = 'top 85%';
  var OUT_DUR   = 0.35; // duration of the exit (scroll-up) fade-out
  var num = function (v, def) { var n = parseFloat(v); return isNaN(n) ? def : n; };

  // Build a repeatable ScrollTrigger using explicit callbacks instead of
  // toggleActions — eliminates the "reversed tween won't restart" GSAP bug.
  // onEnter  → kill any running tween, animate IN  (fresh every time)
  // onLeaveBack → kill any running tween, animate OUT (smooth, not a snap)
  function makeRepeat(trigger, enterFn, leaveFn) {
    var running = null;
    ScrollTrigger.create({
      trigger: trigger,
      start: START,
      onEnter: function () {
        if (running) running.kill();
        running = enterFn();
      },
      onLeaveBack: function () {
        if (running) running.kill();
        running = leaveFn();
      }
    });
  }

  function init() {
    // In Shopify theme editor: reveal everything instantly, skip all motion
    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('[data-zf-anim]').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.clipPath = 'none';
      });
      return;
    }

    var reduce  = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hasGSAP = window.gsap && window.ScrollTrigger;

    if (reduce || !hasGSAP) {
      document.querySelectorAll('[data-zf-anim]').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.clipPath = 'none';
      });
      return;
    }

    var gsap          = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    // ── fade-up ───────────────────────────────────────────────
    document.querySelectorAll('[data-zf-anim="fade-up"]').forEach(function (el) {
      var y   = num(el.dataset.zfAnimY, 32);
      var dur = num(el.dataset.zfAnimDuration, 0.85);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { opacity: 0, y: y });
      makeRepeat(el,
        function () { return gsap.to(el, { opacity: 1, y: 0, duration: dur, delay: del, ease: EASE_OUT }); },
        function () { return gsap.to(el, { opacity: 0, y: y, duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── fade-down ─────────────────────────────────────────────
    document.querySelectorAll('[data-zf-anim="fade-down"]').forEach(function (el) {
      var y   = num(el.dataset.zfAnimY, 20);
      var dur = num(el.dataset.zfAnimDuration, 0.75);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { opacity: 0, y: -y });
      makeRepeat(el,
        function () { return gsap.to(el, { opacity: 1, y: 0, duration: dur, delay: del, ease: EASE_OUT }); },
        function () { return gsap.to(el, { opacity: 0, y: -y, duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── fade ──────────────────────────────────────────────────
    document.querySelectorAll('[data-zf-anim="fade"]').forEach(function (el) {
      var dur = num(el.dataset.zfAnimDuration, 1.0);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { opacity: 0 });
      makeRepeat(el,
        function () { return gsap.to(el, { opacity: 1, duration: dur, delay: del, ease: 'power2.out' }); },
        function () { return gsap.to(el, { opacity: 0, duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── scale-in ─────────────────────────────────────────────
    document.querySelectorAll('[data-zf-anim="scale-in"]').forEach(function (el) {
      var dur = num(el.dataset.zfAnimDuration, 0.9);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { opacity: 0, scale: 0.93 });
      makeRepeat(el,
        function () { return gsap.to(el, { opacity: 1, scale: 1, duration: dur, delay: del, ease: EASE_OUT }); },
        function () { return gsap.to(el, { opacity: 0, scale: 0.93, duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── clip-left (image wipe) ────────────────────────────────
    document.querySelectorAll('[data-zf-anim="clip-left"]').forEach(function (el) {
      var dur = num(el.dataset.zfAnimDuration, 1.1);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { clipPath: 'inset(0 100% 0 0)' });
      makeRepeat(el,
        function () { return gsap.to(el, { clipPath: 'inset(0 0% 0 0)', duration: dur, delay: del, ease: EASE_EXPO }); },
        function () { return gsap.to(el, { clipPath: 'inset(0 100% 0 0)', duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── scale-x (divider line expand) ────────────────────────
    document.querySelectorAll('[data-zf-anim="scale-x"]').forEach(function (el) {
      var dur = num(el.dataset.zfAnimDuration, 0.8);
      var del = num(el.dataset.zfAnimDelay, 0);
      gsap.set(el, { scaleX: 0, transformOrigin: 'center center' });
      makeRepeat(el,
        function () { return gsap.to(el, { scaleX: 1, duration: dur, delay: del, ease: EASE_EXPO }); },
        function () { return gsap.to(el, { scaleX: 0, duration: OUT_DUR, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── counter (number count-up) ─────────────────────────────
    document.querySelectorAll('[data-zf-anim="counter"]').forEach(function (el) {
      var target = num(el.dataset.zfTarget, parseFloat(el.textContent) || 0);
      var suffix = el.dataset.zfSuffix || '';
      var dur    = num(el.dataset.zfAnimDuration, 1.6);
      var obj    = { val: 0 };
      var anim   = null;
      gsap.set(el, { opacity: 0 });
      ScrollTrigger.create({
        trigger: el,
        start: START,
        onEnter: function () {
          if (anim) anim.kill();
          obj.val = 0;
          gsap.to(el, { opacity: 1, duration: 0.3, overwrite: true });
          anim = gsap.to(obj, {
            val: target, duration: dur, ease: 'power2.out',
            onUpdate: function () { el.textContent = Math.round(obj.val).toLocaleString() + suffix; }
          });
        },
        onLeaveBack: function () {
          if (anim) { anim.kill(); anim = null; }
          gsap.to(el, { opacity: 0, duration: OUT_DUR, overwrite: true });
          obj.val = 0;
          el.textContent = '0' + suffix;
        }
      });
    });

    // ── stagger container ─────────────────────────────────────
    document.querySelectorAll('[data-zf-anim="stagger"]').forEach(function (container) {
      var items = container.querySelectorAll('[data-zf-stagger-item]');
      if (!items.length) items = container.children;
      var itemsArr = Array.prototype.slice.call(items);
      var y        = num(container.dataset.zfAnimY, 28);
      var dur      = num(container.dataset.zfAnimDuration, 0.7);
      var stagger  = num(container.dataset.zfStagger, 0.1);
      gsap.set(itemsArr, { opacity: 0, y: y });
      makeRepeat(container,
        function () { return gsap.to(itemsArr, { opacity: 1, y: 0, duration: dur, stagger: stagger, ease: EASE_OUT, overwrite: true }); },
        function () { return gsap.to(itemsArr, { opacity: 0, y: y, duration: OUT_DUR, stagger: 0.04, ease: 'power2.in', overwrite: true }); }
      );
    });

    // ── Parallax drift — scrub, always active ─────────────────
    document.querySelectorAll('[data-zf-parallax]').forEach(function (el) {
      var strength = num(el.dataset.zfParallaxStrength, 0.15);
      gsap.to(el, {
        yPercent: strength * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });

    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  function safeInit() {
    try { init(); } catch (e) {
      console.warn('[zf-anim] init error:', e);
      document.querySelectorAll('[data-zf-anim]').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.clipPath = 'none';
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
